import { Flex } from 'antd';
import Dropdown from '../drop-down/DropDown';
import styles from './FloatingSelect.module.scss';
import Avatar from '../avatar/Avatar';
import Tooltip from '../antd-tooltip/Tooltip';
import { ORDER_TYPE_ICONS } from '../../constants/generic-constants';
import { SelectTick } from '../all-icons/custom-icons';
import Loader from 'components/lead-details-common/loader';
import FlexCenter from 'common/flex-center/FlexCenter';
import cx from 'clsx';
import { Checkbox, IF } from 'common';
import Unavailable from 'common/unavailable';
import Search from 'common/search/Search';
import { useRef, useState } from 'react';

const SelectAllCheck = ({ inputRef, value = [], options = [], handleChange = () => {} }) => {
  const values = value?.map((val) => val?.value);
  const optionValues = options?.map((val) => val?.value);
  const isAllSelected = (options || [])?.every((opt) => values?.includes(opt?.value));
  const indeterminate = !!(value || [])?.filter((val) => optionValues?.includes(val?.value))?.length && !isAllSelected;

  const handleCheckChange = () => {
    if (isAllSelected) {
      handleChange((value || [])?.filter((val) => !optionValues?.includes(val?.value)));
    } else {
      handleChange(options);
    }
    inputRef.current.focus();
  };

  return (
    <Flex gap={12} align="center" className={styles.selectAll}>
      <Checkbox indeterminate={indeterminate} onChange={handleCheckChange} checked={isAllSelected} />
      <div onClick={handleCheckChange} className={`body-14-regular`}>
        Select All
      </div>
    </Flex>
  );
};

const Option = ({
  index,
  inputRef,
  optionRefs,
  highlightedIndex,
  value = [],
  option = [],
  setHighlightedIndex = () => {},
  handleChange = () => {},
  setIsOpen = () => {},
  showSlug = false,
  singleSelect = false,
  searchType = '',
  customRender = null,
}) => {
  const isValueSelected = value?.map((v) => v?.value)?.includes(option?.value);
  const isHighlighted = index === highlightedIndex;
  const type = option?.type ?? option?.product?.type;
  const label =
    option?.product?.type?.slug === 'internal' ? `${option?.product?.name} - ${option?.label}` : option?.label;

  const handleOptionClick = () => {
    if (singleSelect) {
      handleChange(option?.value === value?.[0]?.value ? [] : [option]);
      setIsOpen(false);
      return;
    }
    if (isValueSelected) {
      handleChange(value?.filter((v) => v?.value !== option?.value));
    } else {
      handleChange([...(value || []), option]);
    }
    setHighlightedIndex(index);
    inputRef.current.focus();
  };

  const orderIcon = ORDER_TYPE_ICONS?.[type?.slug];
  const productSrc = option?.images?.[0]?.source_url;

  return (
    <Flex
      role="button"
      key={option?.id || option?.value}
      ref={(el) => (optionRefs.current[index] = el)}
      onClick={handleOptionClick}
      className={cx(styles.hoverOption, isHighlighted ? styles.activeOption : '')}
      align="center"
      justify="space-between"
      gap={16}
    >
      {!customRender ? (
        searchType === 'admin_users' ? (
          <Flex align="center" justify="space" gap={5}>
            <Avatar src={option?.profile_picture} isSystem={true} name={option?.name} />
            <Flex vertical gap={2}>
              <div className="body-14-regular">{option?.showName || option?.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--user-main-text-2)' }}>{option?.email}</div>
            </Flex>
          </Flex>
        ) : (
          <Flex align="center" gap={6}>
            <IF
              condition={productSrc}
              ELSE={
                <IF condition={orderIcon}>
                  <Tooltip title={type?.name}>
                    <div className={styles.orderIcon}>{orderIcon}</div>
                  </Tooltip>
                </IF>
              }
            >
              <img className={'customOptionIcon'} width={40} height={40} src={productSrc} />
            </IF>
            <div>
              <div className={`body-14-regular`}>{label}</div>
              <div style={{ fontSize: '11px', color: 'var(--user-main-text-2)' }}>{showSlug ? option?.slug : ''}</div>
            </div>
          </Flex>
        )
      ) : typeof customRender === 'function' ? (
        customRender(option, isValueSelected)
      ) : null}

      {isValueSelected && <SelectTick stroke={'var(--ant-color-primary)'} style={{ flexShrink: 0 }} />}
    </Flex>
  );
};

const OptionsDropdown = ({
  isOpen = false,
  inputRef: mainInputRef,
  optionRef,
  optionRefs,
  singleSelect,
  value = [],
  setIsOpen = () => {},
  handleChange = () => {},
  onFocus = () => {},
  extraOption = {},
  searchType,
  showSlug,
  highlightedIndex,
  setHighlightedIndex = () => {},
  options,
  containerStyle,
  customRender = null,
  isElastic = false,
}) => {
  const searchInputRef = useRef(null);
  const inputRef = searchInputRef?.current ? searchInputRef : mainInputRef;
  const [search, setSearch] = useState('');

  const filteredOptions = (options || [])?.filter((option) =>
    (option?.label ?? '')
      ?.toLowerCase()
      .replace(/\s/g, '')
      .includes(isElastic ? '' : (search || '')?.toLowerCase()?.replace(/\s/g, '')),
  );

  const handleKeyDown = (e) => {
    setIsOpen(true);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((prev) =>
        prev === -1 ? filteredOptions.length - 1 : (prev - 1 + filteredOptions.length) % filteredOptions.length,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && highlightedIndex !== -1) {
        const option = filteredOptions[highlightedIndex];
        if (singleSelect) {
          handleChange([option]);
        } else {
          const isSelected = value?.some((v) => v.value === option.value);
          if (isSelected) {
            handleChange(value?.filter((v) => v?.value !== option?.value));
          } else {
            handleChange([...(value || []), option]);
          }
        }
        singleSelect && setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const loader = () => (
    <FlexCenter style={{ minHeight: '150px' }}>
      <Loader loaderSize={25} size="small" />
    </FlexCenter>
  );

  const isShowSearchBar = (options || [])?.length > 6 || isElastic;
  const isShowSelectAll = (options || [])?.length !== 0 && !singleSelect;

  const containerClass = cx(
    styles.customOptionContainer,
    styles.show,
    !isShowSearchBar && !isShowSelectAll && styles.normal,
  );

  const { label, onClick } = extraOption;

  return (
    <Dropdown
      placement="bottomRight"
      visible={isOpen}
      dropdownRender={() => (
        <Flex ref={optionRef} gap={12} vertical className={containerClass} style={containerStyle}>
          <IF condition={isShowSearchBar}>
            <div className={styles.selectAll}>
              <Search
                placeholder="Search"
                onChange={(e) => {
                  setSearch(e.target.value);
                  isElastic && onFocus(e.target.value);
                }}
                searchInputRef={searchInputRef}
                value={search}
                allowClear={false}
                style={{ height: '36px', paddingLeft: '12px' }}
                fontClass="body-12-regular"
                iconStyles={{ top: '9px', left: 'unset', right: '9px' }}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
          </IF>
          <IF condition={isShowSelectAll}>
            <SelectAllCheck inputRef={inputRef} value={value} options={filteredOptions} handleChange={handleChange} />
          </IF>

          <Flex vertical gap={1} style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <IF condition={options !== null} ELSE={loader()}>
              <IF
                condition={options?.length !== 0}
                ELSE={
                  <div style={{ marginBlock: '40px' }}>
                    <Unavailable customDesc={'No Data Found'} customGap={10} />
                  </div>
                }
              >
                <IF
                  condition={filteredOptions?.length !== 0}
                  ELSE={
                    <div style={{ marginBlock: '20px' }}>
                      <Unavailable customDesc={'No Option matching your search Found'} customGap={10} />
                    </div>
                  }
                >
                  {filteredOptions?.map((option, index) => (
                    <Option
                      key={option?.id || option?.value}
                      value={value}
                      option={option}
                      optionRef={optionRef}
                      optionRefs={optionRefs}
                      singleSelect={singleSelect}
                      handleChange={handleChange}
                      setIsOpen={setIsOpen}
                      highlightedIndex={highlightedIndex}
                      index={index}
                      searchType={searchType}
                      showSlug={showSlug}
                      setHighlightedIndex={setHighlightedIndex}
                      inputRef={inputRef}
                      customRender={customRender}
                    />
                  ))}
                </IF>
              </IF>
            </IF>
            {label ? (
              <div
                role="button"
                onClick={() => {
                  setIsOpen(false);
                  onClick();
                }}
                className={cx(styles.extraOption, 'body-14-regular')}
              >
                {label}
              </div>
            ) : null}
          </Flex>
        </Flex>
      )}
    />
  );
};

export default OptionsDropdown;
